import React, { useEffect, useState, useCallback } from "react";
import { DatePicker } from "antd";
import { getFilePreSignedUrl } from "../../../utils/api";
import dayjs from "dayjs";
import { LoadingIcon } from "../../../components";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../consts";
import { blobDate, blobSitePrefix } from "../../../utils/demoSiteBlob";

const AveragePercentileCard = ({
  currentOrg,
  currentSite,
  currentCrop,
  currentGreenhouse,
  currentPlot,
  dateList,
  siteMetadata,
}) => {
  const [jsonData, setJsonData] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(null);
  const [jsonTypeOptions, setJsonTypeOptions] = useState([]);
  const [error, setError] = useState(null);
  const [hasLoadedInitially, setHasLoadedInitially] = useState(false);

  const getAndSetJsonPreSignedUrl = useCallback(
    async (url) => {
      try {
        const preSignedUrl = await getFilePreSignedUrl(
          currentOrg,
          currentSite.id,
          currentCrop.name,
          url,
          siteMetadata?.CLOUD_STORAGE_NAME || "azure",
          true,
          true,
        );
        return preSignedUrl?.data?.data?.url;
      } catch {
        return null;
      }
    },
    [currentOrg, currentSite],
  );

  const fetchAndSetJsonFileUrl = useCallback(
    async (baseURL, fileName) => {
      const fileUrl = await getAndSetJsonPreSignedUrl(baseURL + fileName);
      if (fileUrl) {
        const resp = await fetch(fileUrl);
        if (resp.ok) {
          return await resp.json();
        }
      }
      return null;
    },
    [getAndSetJsonPreSignedUrl],
  );

  const tryFetchForDate = useCallback(
    async (dateStr, options) => {
      // Demo/clone sites read another site's blobs (CLOUD_SITE_PREFIX) at that
      // site's dates (CLOUD_DATE_OFFSETS per bay); plain sites are unaffected.
      const baseURL = `${blobSitePrefix(siteMetadata, currentSite)}/${currentCrop.name}/${blobDate(siteMetadata, currentGreenhouse.name, dateStr)}/${currentGreenhouse.name}/${currentPlot.name}/`;
      let tempData = {};
      let allFailed = true;
      const fetchPromises = options.map((opt) =>
        fetchAndSetJsonFileUrl(baseURL, opt.value),
      );
      const results = await Promise.allSettled(fetchPromises);
      results.forEach((r, i) => {
        if (r.status === "fulfilled" && r.value) {
          tempData[options[i].value] = r.value;
          allFailed = false;
        }
      });
      return { data: tempData, allFailed };
    },
    [
      currentSite,
      siteMetadata,
      currentCrop,
      currentGreenhouse,
      currentPlot,
      fetchAndSetJsonFileUrl,
    ],
  );

  useEffect(() => {
    if (siteMetadata && currentCrop) {
      const opts = siteMetadata["average_json_percentile_options"] || [];
      setJsonTypeOptions(opts);
      if (!opts.length) {
        setLoading(false);
      }
    }
  }, [siteMetadata, currentCrop]);

  useEffect(() => {
    if (dateList?.length > 0) {
      const sorted = [...dateList].sort((a, b) => dayjs(b).diff(dayjs(a)));
      setCurrentDate(sorted[0]);
    }
  }, [dateList]);

  // Fallback approach: only on first load
  useEffect(() => {
    if (
      !hasLoadedInitially &&
      currentDate &&
      jsonTypeOptions.length > 0 &&
      currentGreenhouse &&
      currentPlot
    ) {
      fallbackFetchData();
    }
    async function fallbackFetchData() {
      setLoading(true);
      setError(null);

      const sortedDates = [...dateList].sort((a, b) => dayjs(b).diff(dayjs(a)));
      let idx = sortedDates.indexOf(currentDate);
      if (idx < 0) idx = 0;

      let foundValid = false;
      let finalData = {};
      let usedDate = currentDate;

      for (let i = idx; i < sortedDates.length; i++) {
        const attempt = sortedDates[i];
        const { data, allFailed } = await tryFetchForDate(
          attempt,
          jsonTypeOptions,
        );
        if (!allFailed) {
          foundValid = true;
          finalData = data;
          usedDate = attempt;
          break;
        }
      }

      if (!foundValid) {
        setJsonData({});
        setError("Data is not available");
      } else {
        setJsonData(finalData);
        setError(null);
        if (usedDate !== currentDate) {
          setCurrentDate(usedDate);
        }
      }
      setHasLoadedInitially(true);
      setLoading(false);
    }
  }, [
    currentDate,
    jsonTypeOptions,
    currentGreenhouse,
    currentPlot,
    hasLoadedInitially,
    tryFetchForDate,
    dateList,
  ]);

  // If user changes date after we have initial data
  useEffect(() => {
    if (
      hasLoadedInitially &&
      currentDate &&
      jsonTypeOptions.length > 0 &&
      currentGreenhouse &&
      currentPlot
    ) {
      directFetch();
    }
    async function directFetch() {
      setLoading(true);
      setError(null);
      const { data, allFailed } = await tryFetchForDate(
        currentDate,
        jsonTypeOptions,
      );
      if (allFailed) {
        setError("Data is not available");
        setJsonData({});
      } else {
        setJsonData(data);
      }
      setLoading(false);
    }
  }, [
    currentDate,
    hasLoadedInitially,
    currentGreenhouse,
    currentPlot,
    jsonTypeOptions,
    tryFetchForDate,
  ]);

  const disabledDate = (current) => {
    const valid = dateList?.map((d) => dayjs(d));
    return current && !valid.some((v) => v.isSame(current, "day"));
  };

  const handleDateChange = (dt) => {
    if (dt) {
      setCurrentDate(dayjs(dt).format(BACKEND_DATE_FORMAT));
    }
  };

  return (
    <div className="percentage-calculator w-full ">
      <div className="flex justify-between items-center">
        <h2 className="font-bold text-lg mb-3">
          Leaf Length Profile by Percentile
        </h2>
        {currentDate && (
          <DatePicker
            value={currentDate ? dayjs(currentDate) : null}
            className="max-w-32 min-w-[150px]"
            disabledDate={disabledDate}
            onChange={handleDateChange}
            format={UI_DATE_FORMAT}
            placeholder="Select Date"
          />
        )}
      </div>
      {loading ? (
        <LoadingIcon />
      ) : error ? (
        <div className="error-message">{error}</div>
      ) : jsonTypeOptions.length === 0 ? (
        <div className="font-bold text-primaryBlue">No data set to view.</div>
      ) : (
        <>
          {jsonTypeOptions.map((opt) => {
            const data = jsonData[opt.value];
            if (data) {
              const avg = parseFloat(data?.average_length_cm).toFixed(1);
              return (
                <h3 className="mb-2" key={opt.value}>
                  {opt.label} <span className="font-bold">{avg} cm</span>
                </h3>
              );
            }
            return null;
          })}
        </>
      )}
    </div>
  );
};
export { AveragePercentileCard };
